export type Product = {
  coolbluesChoice: CoolbluesChoice;
  alternatives: Alternatives[];
  listPrice: { includingVat: number; excludingVat: number };
  reviewCollection: ReviewCollection[];
  specification: EnergyLabelSpecification;
  secondChanceProductMetadata: SecondChanceProduct;
  subProducts: SubProducts[];
  energyLabel: EnergyLabelType;
  id: string;
  name: string;
  availabilityState: AvailabilityState;
  expectedReleaseDate?: string;
  storeAvailabilities?: StoreAvailability[];
  navigation: {
    sitemapLinks: SitemapLinks;
  };
  primaryImage: {
    id: string;
  };
  productType: {
    id: string;
    productTypeId: number;
    singularName: string;
    pluralName: string;
  };
  prosAndCons: {
    pros: string[];
    cons: string[];
  };
  manual: {
    manualUrl: string;
    fileSizeBytes: number;
  };
  includedInBox: string[];
  mainText: string;
  properties: {
    propertyGroups: PropertyGroup[];
    summary: Property[];
  };
  recommendedRetailSalesPrice: RecommendedRetailSalesPrice;
  salesPrice: SalesPrice;
  urls: {
    slug: string;
    product: string;
  };
  reviewMetaData: Reviews;
  reviewMetadata: Reviews;
  reviews: Reviews;
  services: Services;
  mediaGallery: MediaGallery;
  promoIcon?: PromoIcon;
  subscription: Subscription;
  insurance: Insurance | null;
  isComposed: boolean;
  primaryPromoIcon?: FilterPagePromoIcon;
  secondaryPromoIcon?: FilterPagePromoIcon;
  promotionalContent: PromotionalContent;
};

export type Subscription = {
  installmentAmount: number;
  oneTimePaymentAmount: number;
};

export type MediaGallery = {
  items: GalleryItem[];
  mainGalleryItem: GalleryItem;
};

export type GalleryItem = ProductYoutubeVideo | ProductImage;

export type ProductYoutubeVideo = {
  __typename: string;
  image: CDNImage;
  id: string;
};

export type ProductImage = {
  __typename: 'ProductImage' | 'ProductYoutubeVideo';
  image: CDNImage;
  label: string;
};

export type CDNImage = {
  id: string;
  collectionName: CollectionName;
};

export type CollectionName =
  | 'Content'
  | 'Products'
  | 'Reviews'
  | 'SecondChance'
  | 'Temp'
  | 'DeliveryInstallation'
  | 'Animations';

export type FormattedCollectionName = 'content' | 'product' | 'review' | 'second_chance_product';

export type PromoIcon = {
  image: { id: string; collectionName: FormattedCollectionName } | null;
  displayText: string | null;
  explanation: string | null;
};

export type Services = {
  isPreselected: boolean;
  serviceExtendedUsps: {
    description: string;
    title: string;
    sortOrder: string;
  }[];
}[];

export type Reviews = {
  averageRating: number;
  count: number;
  reviewAveragePerCategory: ReviewAveragePerCategory[];
  reviewCountPerRating: ReviewCountPerRating[];
  reviews: ProductInformationReviewType[];
};

export type Property = {
  displayName: string;
  hasExplanation: boolean;
  propertyId: string;
  value: string;
};

export type PropertyGroup = {
  properties: Property[];
  title: string;
};

type ReviewAveragePerCategory = {
  averageRating: number;
  name: string;
};

type ReviewCountPerRating = {
  count: number;
  stars: string;
};

export type ProductPageQueryOutput = {
  product: Product | null;
};

export type EnergyLabelVersion = 'default' | 'legacy';

export type EnergyLabelType = {
  specification: EnergyLabelSpecification;
  offset: number;
  level: number;
  version: EnergyLabelVersion;
  image?: { id: string };
};

export type EnergyLabelSpecification = {
  absoluteUrl: string;
};

export type RecommendedRetailSalesPrice = {
  includingVat: number;
  excludingVat: number;
};

export type SalesPrice = {
  currency?: string;
  includingVat: number;
  excludingVat: number;
};

export type SubProducts = {
  product: Product;
};

export type SecondChanceProduct = {
  id: string;
  salesPrice: SalesPrice;
  secondChanceProductsCount?: number;
};

export type SitemapLinks = {
  brandProductType?: NavigationNode;
  brandProductTypeSegment?: NavigationNode;
};

type NavigationNode = {
  label: string;
  path: string;
};

type ReviewCollection = {
  id: number;
  title: string;
  text: string;
  pros: string[];
  cons: string[];
  authorName: string;
  ratingAverage: number;
  translatedFrom: 'nl' | 'en' | 'de' | null;
  createdAt: string;
};

type ProductInformationReviewType = {
  title: string;
  text: string;
  id: number;
  authorName: string;
  expertReview?: boolean;
  pros: string[];
  cons: string[];
  ratingAverage: number;
  isTranslated?: boolean;
  translatedFrom: 'nl' | 'en' | 'de' | null;
  starsAverage?: number;
  createdAt: string;
  discriminantValue: string;
  images: string[];
  productNameWrittenFor?: string;
  votes: {
    up: number;
    down: number;
  };
};

export type ExtendedUsp = {
  extendedUspId?: number;
  title: string;
  description: string;
  sortOrder?: string;
};

export type StoreAvailability = {
  stockLocationId: number;
  availabilityState: AvailabilityState;
  store: Store;
};

export type Store = {
  name: string;
  address: {
    city: string;
    street: string;
    houseNumber: string;
    houseNumberAddition: string;
    postalCode: string;
  };
  openingHours: StoreOpeningHour[];
};

export type StoreOpeningHour = {
  scheduleDate: string;
  openTime: string;
  closeTime: string;
};

export enum AvailabilityState {
  AvailableSoon = 'AvailableSoon',
  OnStock = 'OnStock',
  TemporaryOutOfStock = 'TemporaryOutOfStock',
  OutOfStock = 'OutOfStock',
  Historic = 'Historic',
  PreOrder = 'PreOrder'
}

type InsuranceCondition = {
  id: string;
  description: string;
};

export type InsuranceLiability = {
  damage: number;
  theft: number;
};

type InsurancePrice = {
  includingVat: number;
  excludingVat: number;
};

export type InsuranceAttribute = {
  attributeId: number;
  icon: string;
  name: string;
  description: string;
};

type InsurancePackage = {
  title: string;
  products: InsuranceProduct[];
  attributes: InsuranceAttribute[];
  conditions: InsuranceCondition[];
  usps: string[];
  liability: InsuranceLiability;
  isRecommended: boolean;
};

export type InsuranceProduct = {
  title: string;
  paymentType: 'one-time' | 'monthly';
  durationInYears: number;
  attributes: InsuranceAttribute[];
  liability: InsuranceLiability;
  premium: InsurancePrice;
  yearlyPremium: InsurancePrice;
  usps: string[];
  isRecommended: boolean;
};

export type Insurance = {
  description: string;
  packages: InsurancePackage[];
  conditions: string[];
  summary: {
    requiredAttributes: InsuranceAttribute[];
    optionalAttributes: InsuranceAttribute[];
    liability: InsuranceLiability;
    conditions: InsuranceCondition[];
    liabilityCondition: InsuranceCondition | null;
  };
};
export type Alternatives = {
  product: Product;
  distinctProperties: DistinctProperties[];
};

export type DistinctProperties = {
  displayName: string;
  hasExplanation: boolean;
  propertyId: string;
  value: string;
};

export type CoolbluesChoice = {
  content: {
    title?: string;
    explanation?: string;
    explanationBullets?: string[];
    specialist?: {
      image: CDNImage;
    };
    type: CoolbluesChoiceType;
  };
};

export type CoolbluesChoiceType = 'Normal' | 'Green';

export type FilterPagePromoIcon = {
  imageId: string;
  text: string;
  type:
    | 'BlackFriday'
    | 'CyberMonday'
    | 'Normal'
    | 'Solden'
    | 'Green'
    | 'CoolbluesGreenChoice'
    | 'CoolbluesChoice'
    | 'Text'
    | 'Offer'
    | 'Image'
    | 'ManufacturerIcon';
};

export type PromotionalContent = {
  promoIcon?: PromotionalContentPromoIcon;
  promoLabel?: PromotionalContentPromoLabel;
};

export type PromotionalContentPromoIcon = {
  altText: string;
  image: {
    id: string;
    collectionName: CollectionName;
  };
  details?: {
    title: string;
    explanation: string;
  };
};

export type PromotionalContentPromoLabel = {
  label: string;
  details?: {
    title: string;
    explanation: string;
  };
};
